// @ts-nocheck
import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Loader2, Play, Square, Database, AlertCircle, CheckCircle2, Clock, BarChart3, ListFilter, RotateCcw, Activity } from "lucide-react";
import { useToast } from "../../../contexts/ToastContext";
import { api } from "../../../services/api";

export function StockbitMigrationControl() {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [status, setStatus] = useState<any>(null);
  const [envConfig, setEnvConfig] = useState({
    token: "",
    workers: 5,
    rps: 5
  });
  const [dateRange, setDateRange] = useState({
    start: "2020-02-10",
    end: "2025-09-19"
  });
  const [aggregateDate, setAggregateDate] = useState('2020-02-11');
  const [aggregationStatus, setAggregationStatus] = useState<any[]>([]);
  const [failedTasks, setFailedTasks] = useState<any[]>([]);

  const loadStatus = async () => {
    try {
      const result = await api.getMigrationStatus();
      if (result.success && result.data) {
        setStatus(result.data);
        if (result.data.config) {
          setEnvConfig(prev => ({
            token: prev.token || result.data.config.current_token || "",
            workers: result.data.config.max_workers || 5,
            rps: result.data.config.max_rps || 5
          }));
        }
      }

      // Load Aggregation Status
      const aggResult = await api.getMigrationAggregationStatus();
      if (aggResult.success) setAggregationStatus(aggResult.data || []);

      // Load Failed Tasks
      const failResult = await api.getMigrationFailedTasks(1, 10);
      if (failResult.success) setFailedTasks(failResult.data || []);

    } catch (err: any) {
      console.error("Failed to load migration status:", err);
    }
  };

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSeed = async () => {
    if (!window.confirm(`Yakin ingin melakukan seeding tugas dari ${dateRange.start} sampai ${dateRange.end}? Ini akan memakan waktu beberapa menit.`)) return;
    
    setActionLoading('seed');
    try {
      const result = await api.seedMigration(dateRange.start, dateRange.end);
      if (result.success) {
        showToast({
          type: 'success',
          title: 'Seeding Berhasil',
          message: `Berhasil menambahkan ${result.data?.totalTasks || 0} tugas ke antrean.`
        });
        loadStatus();
      } else {
        showToast({ type: 'error', title: 'Seeding Gagal', message: result.error || 'Terjadi kesalahan' });
      }
    } catch (err: any) {
      showToast({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSmartSeed = async () => {
    if (!window.confirm(`Smart Seed: Akan cek Azure dulu, lalu hanya seed tanggal yang belum ada (±1 hari). Range: ${dateRange.start} s/d ${dateRange.end}. Lanjutkan?`)) return;
    
    setActionLoading('smart-seed');
    try {
      const result = await api.seedMigrationSmart(dateRange.start, dateRange.end);
      if (result.success) {
        const data = result.data;
        const msg = data?.totalTasks === 0
          ? `Semua tanggal sudah ada di Azure (${data.existingDates} tanggal). Tidak ada yang perlu di-seed.`
          : `Berhasil seed ${data?.totalTasks || 0} tugas. Existing: ${data?.existingDates || 0}, Missing: ${data?.missingDates || 0}, Seeded dates: ${data?.expandedDates?.length || 0}`;
        showToast({
          type: 'success',
          title: 'Smart Seeding Berhasil',
          message: msg
        });
        loadStatus();
      } else {
        showToast({ type: 'error', title: 'Smart Seeding Gagal', message: result.error || 'Terjadi kesalahan' });
      }
    } catch (err: any) {
      showToast({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAction = async (action: 'start' | 'stop') => {
    setActionLoading(action);
    try {
      const result = action === 'start' ? await api.startMigration() : await api.stopMigration();
      if (result.success) {
        showToast({
          type: 'success',
          title: 'Berhasil',
          message: `Migrasi berhasil ${action === 'start' ? 'dijalankan' : 'dihentikan'}`
        });
        loadStatus();
      } else {
        showToast({ type: 'error', title: 'Gagal', message: result.error || `Gagal ${action} migrasi` });
      }
    } catch (err: any) {
      showToast({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateToken = async () => {
    setActionLoading('token');
    try {
      const result = await api.updateMigrationConfig({ 
          token: envConfig.token,
          maxWorkers: envConfig.workers,
          maxRps: envConfig.rps
      });
      if (result.success) {
        showToast({ type: 'success', title: 'Config Diupdate', message: 'Environment Config berhasil diperbarui' });
      } else {
        showToast({ type: 'error', title: 'Gagal', message: result.error || 'Gagal update config' });
      }
    } catch (err: any) {
      showToast({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleTriggerAggregation = async (type: 'IDX' | 'SECTOR') => {
    setActionLoading(`aggregate_${type}`);
    try {
      const result = await api.triggerAggregationRange(dateRange.start, dateRange.end, type);
      if (result.success) {
        showToast({ type: 'success', title: 'Berhasil', message: `Mass ${type} Aggregation started for ${dateRange.start} to ${dateRange.end}` });
        loadStatus();
      } else {
        showToast({ type: 'error', title: 'Gagal', message: result.error || `Failed to start ${type} aggregation` });
      }
    } catch (err: any) {
      showToast({ type: 'error', title: 'Error', message: err.message || `Failed to start ${type} aggregation` });
    } finally {
      setActionLoading(null);
    }
  };

  const isRunning = status?.config?.is_running;
  const summary = status?.summary || [];
  
  const getCount = (s: string) => summary.find((item: any) => item.status === s)?.total_tasks || 0;
  const total = summary.reduce((acc: number, item: any) => acc + parseInt(item.total_tasks), 0);
  const completed = getCount('COMPLETED');
  const failed = getCount('FAILED');
  const skipped = getCount('SKIPPED');
  const notFound = getCount('NOT_FOUND');
  const pending = getCount('PENDING');
  const processing = getCount('PROCESSING');

  const progressPercent = total > 0 ? Math.round(((completed + skipped + notFound) / total) * 100) : 0;

  const idxStatus = aggregationStatus.filter(a => a.type === 'IDX');
  const sectorStatus = aggregationStatus.filter(a => a.type === 'SECTOR');

  return (
    <Card className="mb-6 overflow-hidden border-t-4 border-t-purple-600 shadow-lg bg-[#0f172a] text-slate-200">
      <CardHeader className="bg-slate-900/50 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/10 rounded-lg">
              <Database className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <CardTitle className="text-xl text-white">Stockbit History Migration</CardTitle>
              <CardDescription className="text-slate-400">Migrate 6 years of Broker Summary to Azure</CardDescription>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 ${isRunning ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'}`}>
              {isRunning ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
              {isRunning ? 'MIGRATING' : 'IDLE'}
            </div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-purple-400">
              {progressPercent}% Complete
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {/* Progress Bar */}
        <div className="w-full bg-slate-800 h-3 rounded-full mb-6 overflow-hidden border border-slate-700">
          <div 
            className="bg-purple-500 h-full transition-all duration-1000 ease-in-out shadow-[0_0_10px_rgba(168,85,247,0.5)]" 
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Side: Stats & Config */}
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-slate-800/40 rounded-xl border border-slate-700/50 text-center">
                <p className="text-[10px] text-slate-400 uppercase font-bold mb-1">Total Tasks</p>
                <p className="text-lg font-mono text-white">{total.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-green-500/5 rounded-xl border border-green-500/20 text-center">
                <p className="text-[10px] text-green-400 uppercase font-bold mb-1">Success</p>
                <p className="text-lg font-mono text-green-400">{completed.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-red-500/5 rounded-xl border border-red-500/20 text-center">
                <p className="text-[10px] text-red-400 uppercase font-bold mb-1">Failed</p>
                <p className="text-lg font-mono text-red-400">{failed.toLocaleString()}</p>
              </div>
            </div>

              <div className="p-4 bg-slate-900/40 rounded-xl border border-slate-700 space-y-4">
                <p className="text-xs font-bold text-slate-300 uppercase flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5 text-purple-400" />
                  Broksum Environment Config
                </p>
                <div className="space-y-1.5">
                  <label className="text-[10px] text-slate-500 uppercase">Stockbit Token (Bearer)</label>
                  <Input 
                    type="text"
                    placeholder="Enter Stockbit Bearer Token..." 
                    value={envConfig.token} 
                    onChange={e => setEnvConfig({...envConfig, token: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-xs h-9 font-mono"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] text-slate-500 uppercase">Max Workers</label>
                    <Input 
                      type="number" 
                      value={envConfig.workers} 
                      onChange={e => setEnvConfig({...envConfig, workers: parseInt(e.target.value) || 1})}
                      className="bg-slate-800 border-slate-700 text-xs h-9"
                      min="1"
                      max="20"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] text-slate-500 uppercase">Max RPS</label>
                    <Input 
                      type="number" 
                      value={envConfig.rps} 
                      onChange={e => setEnvConfig({...envConfig, rps: parseInt(e.target.value) || 1})}
                      className="bg-slate-800 border-slate-700 text-xs h-9"
                      min="1"
                      max="20"
                    />
                  </div>
                </div>

                <Button 
                  onClick={handleUpdateToken}
                  disabled={actionLoading === 'token'}
                  className="w-full bg-purple-600 hover:bg-purple-700 h-9 rounded-lg text-xs font-bold transition-all active:scale-95"
                >
                  {actionLoading === 'token' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : "Save Config"}
                </Button>
                <p className="text-[10px] text-slate-500 italic">
                  * Token ini akan digunakan secara otomatis oleh worker yang sedang berjalan.
                </p>
              </div>

              <div className="p-4 bg-slate-900/40 rounded-xl border border-slate-700 space-y-4">
              <p className="text-xs font-bold text-slate-300 uppercase flex items-center gap-2">
                <BarChart3 className="w-3.5 h-3.5 text-purple-400" />
                Broksum Dates Seeding
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] text-slate-500 uppercase">Start Date</label>
                  <Input 
                    type="date" 
                    value={dateRange.start} 
                    onChange={e => setDateRange({...dateRange, start: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-xs h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] text-slate-500 uppercase">End Date</label>
                  <Input 
                    type="date" 
                    value={dateRange.end} 
                    onChange={e => setDateRange({...dateRange, end: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-xs h-9"
                  />
                </div>
              </div>
              <Button 
                onClick={handleSeed}
                disabled={actionLoading === 'seed' || actionLoading === 'smart-seed' || isRunning}
                className="w-full bg-indigo-600 hover:bg-indigo-700 h-9 rounded-lg text-xs font-bold transition-all active:scale-95"
              >
                {actionLoading === 'seed' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <RotateCcw className="w-3.5 h-3.5 mr-2" />}
                Re-Seed Queue (Force All)
              </Button>
              <Button 
                onClick={handleSmartSeed}
                disabled={actionLoading === 'seed' || actionLoading === 'smart-seed' || isRunning}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-9 rounded-lg text-xs font-bold transition-all active:scale-95"
              >
                {actionLoading === 'smart-seed' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <Database className="w-3.5 h-3.5 mr-2" />}
                Smart Seed (Skip Existing ±1 Day)
              </Button>
            </div>

            <div className="p-4 bg-slate-900/40 rounded-xl border border-slate-700 space-y-4">
              <p className="text-xs font-bold text-slate-300 uppercase flex items-center gap-2">
                <Database className="w-3.5 h-3.5 text-orange-400" />
                Manual Mass Aggregation
              </p>
              <p className="text-[10px] text-slate-400">
                Akan mengagregasi data massal sesuai dengan rentang <strong>Seeding Configuration</strong> ({dateRange.start} s/d {dateRange.end}).
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  onClick={() => handleTriggerAggregation('IDX')}
                  disabled={actionLoading === 'aggregate_IDX'}
                  className="w-full bg-orange-600 hover:bg-orange-700 h-9 rounded-lg text-xs font-bold transition-all active:scale-95"
                >
                  {actionLoading === 'aggregate_IDX' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <Activity className="w-3.5 h-3.5 mr-2" />}
                  Trigger IDX
                </Button>
                <Button 
                  onClick={() => handleTriggerAggregation('SECTOR')}
                  disabled={actionLoading === 'aggregate_SECTOR'}
                  className="w-full bg-blue-600 hover:bg-blue-700 h-9 rounded-lg text-xs font-bold transition-all active:scale-95"
                >
                  {actionLoading === 'aggregate_SECTOR' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <Activity className="w-3.5 h-3.5 mr-2" />}
                  Trigger Sector
                </Button>
              </div>
              <p className="text-[10px] text-slate-500 italic">
                * Eksekusi manual ini berjalan di background dan memproses tanggal secara berurutan.
              </p>
            </div>
          </div>

          {/* Right Side: Actions & Workers */}
          <div className="space-y-6">
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  onClick={() => handleAction('start')} 
                  disabled={isRunning || actionLoading !== null || total === 0}
                  className="h-12 bg-green-600 hover:bg-green-700 text-white border-none rounded-xl transition-all active:scale-95 disabled:opacity-50"
                >
                  {actionLoading === 'start' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  Start Migration
                </Button>
                <Button 
                  onClick={() => handleAction('stop')} 
                  disabled={!isRunning || actionLoading !== null}
                  variant="destructive"
                  className="h-12 bg-red-600 hover:bg-red-700 text-white border-none rounded-xl transition-all active:scale-95 disabled:opacity-50"
                >
                  {actionLoading === 'stop' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Square className="w-4 h-4 mr-2" />}
                  Stop Workers
                </Button>
              </div>
              
              <div className="p-3 bg-slate-800/30 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-xs text-slate-400">Active Workers</span>
                <span className="text-sm font-mono text-purple-400 font-bold">{isRunning ? status?.config?.max_workers : 0}</span>
              </div>
              <div className="p-3 bg-slate-800/30 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-xs text-slate-400">Target Throughput</span>
                <span className="text-sm font-mono text-purple-400 font-bold">{status?.config?.max_rps} RPS</span>
              </div>

              {/* Active Workers Tasks */}
              {status?.activeTasks?.length > 0 && (
                <div className="space-y-2 pt-2">
                  <p className="text-[10px] font-bold text-green-400 uppercase flex items-center gap-2">
                    <Activity className="w-3.5 h-3.5 animate-pulse" />
                    Worker Activity
                  </p>
                  <div className="max-h-[300px] overflow-y-auto space-y-1.5 scrollbar-thin scrollbar-thumb-slate-700 pr-1">
                    {status.activeTasks.map((task: any, idx: number) => (
                      <div key={idx} className="text-[10px] p-2 bg-green-500/5 border border-green-500/10 rounded-lg flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className="px-1.5 py-0.5 bg-green-500/20 rounded text-[9px] text-green-300 font-mono">{task.worker_id}</span>
                          <span className="text-white font-semibold">{task.stock_code}</span>
                        </div>
                        <span className="text-slate-500">{task.date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Aggregation Monitor */}
            {(idxStatus.length > 0 || sectorStatus.length > 0) && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-orange-400 uppercase flex items-center gap-2">
                    <BarChart3 className="w-3.5 h-3.5" />
                    IDX Progress
                  </p>
                  <div className="space-y-1.5">
                    {idxStatus.map((agg: any, idx: number) => (
                      <div key={idx} className="text-[10px] p-2 bg-orange-500/5 border border-orange-500/10 rounded-lg flex justify-between items-center">
                        <span className="font-mono text-white">{agg.date}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                          agg.status === 'COMPLETED' ? 'bg-green-500/20 text-green-300' : 
                          agg.status === 'RUNNING' ? 'bg-blue-500/20 text-blue-300 animate-pulse' : 
                          agg.status === 'FAILED' ? 'bg-red-500/20 text-red-300' :
                          'bg-slate-500/20 text-slate-300'
                        }`}>
                          {agg.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-blue-400 uppercase flex items-center gap-2">
                    <BarChart3 className="w-3.5 h-3.5" />
                    Sector Progress
                  </p>
                  <div className="space-y-1.5">
                    {sectorStatus.map((agg: any, idx: number) => (
                      <div key={idx} className="text-[10px] p-2 bg-blue-500/5 border border-blue-500/10 rounded-lg flex justify-between items-center">
                        <span className="font-mono text-white">{agg.date}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                          agg.status === 'COMPLETED' ? 'bg-green-500/20 text-green-300' : 
                          agg.status === 'RUNNING' ? 'bg-blue-500/20 text-blue-300 animate-pulse' : 
                          agg.status === 'FAILED' ? 'bg-red-500/20 text-red-300' :
                          'bg-slate-500/20 text-slate-300'
                        }`}>
                          {agg.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Error & Failed Monitor */}
            {failedTasks.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] font-bold text-red-400 uppercase flex items-center gap-2">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Failed / Skipped Tracker
                </p>
                <div className="max-h-[150px] overflow-y-auto space-y-1.5 scrollbar-thin scrollbar-thumb-slate-700">
                  {failedTasks.map((err: any, idx: number) => (
                    <div key={idx} className="text-[10px] p-2 bg-red-500/5 border border-red-500/10 rounded-lg space-y-1">
                      <div className="flex justify-between">
                        <span className="font-mono text-white">{err.date} <span className="text-red-400">{err.stock_code}</span></span>
                        <span className="text-[9px] uppercase text-slate-500">{err.status}</span>
                      </div>
                      <p className="text-red-300/70 italic line-clamp-1">{err.error_log || 'No data found'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 p-4 bg-purple-500/5 border border-purple-500/20 rounded-xl">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-purple-400 shrink-0 mt-0.5" />
            <div className="text-xs text-purple-200/70 leading-relaxed">
              <p className="font-semibold text-purple-300 mb-1">Migration Protocol:</p>
              This process utilizes a distributed worker pool with atomic task locking. It is designed to run 
              concurrently with the main TICMI scheduler without interference. Ensure the Stockbit Token is 
              updated periodically via the <strong>Stockbit Config</strong> section.
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
